import React, { useState, useEffect, useRef } from 'react';
import { QuestionType, getQuestionTypeName } from '../../models/types';
import styles from './Questions.module.css';

/**
 * Simplified Questions Component for Demo Mode
 * 
 * This is a lightweight version of the full Questions component,
 * designed specifically for the landing page demo.
 */

const Questions = ({ demoMode, demoModuleVersion, onDemoComplete, onDemoExit }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState(() => {
    const stored = localStorage.getItem('demo_user_answers');
    return stored ? JSON.parse(stored) : {};
  });
  const [goToInput, setGoToInput] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showGoToModal, setShowGoToModal] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showShakeAnimation, setShowShakeAnimation] = useState(false);
  const [magnifiedImage, setMagnifiedImage] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [startTime] = useState(new Date().toISOString());
  const [showColon, setShowColon] = useState(true);
  const [isTimeBoxHidden, setIsTimeBoxHidden] = useState(false);
  
  // Image modal drag handling
  const [isDragging, setIsDragging] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const imageContainerRef = useRef(null);

  const currentQuestion = demoModuleVersion?.questions[currentQuestionIndex];
  const totalQuestions = demoModuleVersion?.questions.length || 0;

  // Define handleSubmit early so useEffect can reference it
  const handleSubmit = React.useCallback(() => {
    if (onDemoComplete) {
      onDemoComplete(userAnswers);
    }
  }, [onDemoComplete, userAnswers]);

  // Timer countdown - matches original implementation
  useEffect(() => {
    if (!demoModuleVersion?.durationInMinutes) return;

    const updateTimer = () => {
      // Calculate remaining time based on start time and duration
      const now = new Date();
      const startDate = new Date(startTime);
      const endTime = new Date(startDate.getTime() + (demoModuleVersion.durationInMinutes * 60 * 1000));
      const remainingMs = endTime.getTime() - now.getTime();
      
      // Toggle colon visibility for blinking effect
      setShowColon(prev => !prev);
      
      if (remainingMs <= 0) {
        setTimeRemaining({
          hasTimeRemaining: false,
          isExpired: true,
          totalMinutes: 0,
          hours: 0,
          minutes: 0,
          formattedTime: '00:00',
          formattedDisplay: 'Time Expired'
        });
        handleSubmit();
        return;
      }
      
      const totalMinutesRemaining = Math.ceil(remainingMs / (1000 * 60));
      const hours = Math.floor(totalMinutesRemaining / 60);
      const minutes = totalMinutesRemaining % 60;
      
      const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      const formattedDisplay = hours > 0 
        ? `${hours}h ${minutes}m remaining` 
        : `${minutes}m remaining`;
      
      setTimeRemaining({
        hasTimeRemaining: true,
        isExpired: false,
        totalMinutes: totalMinutesRemaining,
        hours,
        minutes,
        formattedTime,
        formattedDisplay
      });
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [demoModuleVersion, startTime, handleSubmit]);

  // Handle escape key for closing magnified image
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && magnifiedImage) {
        event.preventDefault();
        event.stopPropagation();
        setMagnifiedImage(null);
      }
    };

    if (magnifiedImage) {
      document.addEventListener('keydown', handleEscapeKey, true);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey, true);
      };
    }
  }, [magnifiedImage]);

  // Save answers to localStorage
  useEffect(() => {
    localStorage.setItem('demo_user_answers', JSON.stringify(userAnswers));
  }, [userAnswers]);

  const handleAnswerSelect = (answerId) => {
    const questionId = currentQuestion.id;
    const questionType = currentQuestion.type;

    if (questionType === QuestionType.SingleChoice) {
      // Single choice - replace answer
      setUserAnswers(prev => ({
        ...prev,
        [questionId]: [answerId]
      }));
    } else if (questionType === QuestionType.MultipleChoice) {
      // Multiple choice - toggle answer
      setUserAnswers(prev => {
        const current = prev[questionId] || [];
        const updated = current.includes(answerId)
          ? current.filter(id => id !== answerId)
          : [...current, answerId];
        return {
          ...prev,
          [questionId]: updated
        };
      });
    }
  };

  // Image modal drag handlers
  const handleMouseDown = (e) => {
    if (!imageContainerRef.current) return;
    e.stopPropagation(); // Prevent closing modal when starting drag
    setIsMouseDown(true);
    setIsDragging(false);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      scrollLeft: imageContainerRef.current.scrollLeft,
      scrollTop: imageContainerRef.current.scrollTop
    });
  };

  const handleMouseMove = (e) => {
    if (!isMouseDown || !imageContainerRef.current) return;
    const dx = Math.abs(e.clientX - dragStart.x);
    const dy = Math.abs(e.clientY - dragStart.y);
    
    // Consider it a drag if moved more than 5 pixels
    if (dx > 5 || dy > 5) {
      setIsDragging(true);
      e.preventDefault();
      e.stopPropagation();
      imageContainerRef.current.scrollLeft = dragStart.scrollLeft - (e.clientX - dragStart.x);
      imageContainerRef.current.scrollTop = dragStart.scrollTop - (e.clientY - dragStart.y);
    }
  };

  const handleMouseUp = (e) => {
    setIsMouseDown(false);
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation(); // Prevent closing modal after drag
      // Use a small delay to prevent click event from firing
      setTimeout(() => setIsDragging(false), 100);
    }
  };

  const handleMouseLeave = () => {
    setIsMouseDown(false);
    setIsDragging(false);
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const triggerShakeAndError = () => {
    setShowShakeAnimation(true);
    setShowErrorPopup(true);
    
    const element = document.querySelector('.go-to-container');
    if (element) {
      element.style.animation = 'none';
      void element.offsetHeight;
      element.style.animation = 'shake 0.5s ease-in-out';
    }
    
    setTimeout(() => {
      setShowShakeAnimation(false);
      if (element) {
        element.style.animation = '';
      }
    }, 500);
    
    setTimeout(() => {
      setShowErrorPopup(false);
    }, 3000);
  };

  const handleGoToSubmit = () => {
    const num = parseInt(goToInput);
    if (num >= 1 && num <= totalQuestions) {
      setCurrentQuestionIndex(num - 1);
      setGoToInput('');
      setShowErrorPopup(false);
      setShowGoToModal(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      triggerShakeAndError();
    }
  };

  const handleGoToInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setGoToInput(value);
      
      if (value !== '') {
        const questionNumber = parseInt(value);
        if (!isNaN(questionNumber) && (questionNumber < 1 || questionNumber > totalQuestions)) {
          triggerShakeAndError();
        } else {
          setShowErrorPopup(false);
        }
      }
    }
  };

  const handleGoToKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleGoToSubmit();
    }
  };

  const handleSubmitClick = () => {
    setShowSubmitModal(true);
  };

  const isAnswered = (index) => {
    const question = demoModuleVersion.questions[index];
    const answer = userAnswers[question.id];
    return answer && answer.length > 0;
  };

  const getUnansweredQuestions = () => {
    return demoModuleVersion.questions
      .map((q, i) => ({ question: q, index: i }))
      .filter(({ question }) => !userAnswers[question.id] || userAnswers[question.id].length === 0);
  };

  if (!demoModuleVersion) {
    return <div className={styles.container}>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      {/* Demo Banner */}
      <div className={styles.demoBanner}>
        <span>🎓 Demo Mode</span>
        <button 
          className={styles.backHomeButton}
          onClick={onDemoExit}
          title="Back to Home"
        >
          ← Home
        </button>
      </div>

      {/* Header with Timer and Progress */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.moduleTitle}>{demoModuleVersion.title}</h2>
          <p className={styles.questionCounter}>
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </p>
        </div>
        
        <div className={styles.headerRight}>
          {timeRemaining && !timeRemaining.isExpired && (
            <div 
              className={styles.timer}
              onClick={() => setIsTimeBoxHidden(!isTimeBoxHidden)}
              title={isTimeBoxHidden ? "Click to show remaining time" : "Click to hide remaining time"}
            >
              <div className={styles.timerIcon}>⏱</div>
              <div className={styles.timerContent}>
                <div className={styles.timerText}>
                  {isTimeBoxHidden 
                    ? '--:--' 
                    : timeRemaining.formattedTime.replace(/:/g, showColon ? ':' : ' ')
                  }
                </div>
                <div className={styles.timerHint}>
                  Click to {isTimeBoxHidden ? 'show' : 'hide'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className={styles.progressBar}>
        <div className={styles.progressContainer}>
          {demoModuleVersion.questions.map((q, i) => {
            const answered = isAnswered(i);
            const isPast = i < currentQuestionIndex;
            const isCurrent = i === currentQuestionIndex;
            const isSkipped = !answered && isPast;
            const segmentWidth = (1 / demoModuleVersion.questions.length) * 100;
            
            return (
              <div
                key={q.id}
                className={`${styles.progressSegment} ${
                  answered ? styles['progressSegment--answered'] : ''
                } ${
                  isSkipped ? styles['progressSegment--skipped'] : ''
                } ${
                  isCurrent ? styles['progressSegment--current'] : ''
                } ${
                  isSkipped ? styles['progressSegment--clickable'] : ''
                }`}
                style={{ width: `${segmentWidth}%` }}
                onClick={isSkipped ? () => setCurrentQuestionIndex(i) : undefined}
                title={isSkipped ? `Go to Question ${i + 1} (Skipped)` : undefined}
              />
            );
          })}
        </div>
        <div className={styles.progressInfo}>
          <span className={styles.progressText}>
            Question {currentQuestionIndex + 1} of {demoModuleVersion.questions.length}
          </span>
        </div>
      </div>

      {/* Question Content */}
      <div className={styles.questionContainer}>
        <div className={styles.questionHeader}>
          <h3>Question {currentQuestionIndex + 1}</h3>
          <div className={styles.questionType}>
            {getQuestionTypeName(currentQuestion.type)}
          </div>
        </div>

        {/* Separator after question header */}
        <div className={styles.questionHeaderSeparator}></div>

        {currentQuestion.text && (
          <div className={styles.questionText}>
            {currentQuestion.text}
          </div>
        )}

        {/* Question Attachments */}
        {currentQuestion.staticFileUrls && currentQuestion.staticFileUrls.length > 0 && (
          <div className={styles.questionAttachments}>
            <div 
              className={styles.filesGrid} 
              style={{
                gridTemplateColumns: currentQuestion.staticFileUrls.length > 1 
                  ? 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))' 
                  : '1fr',
                maxWidth: '100%'
              }}
            >
              {currentQuestion.staticFileUrls.map((url, index) => {
                const ext = url.split('.').pop().toLowerCase();
                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
                const isAudio = ['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext);
                const isVideo = ['mp4', 'avi', 'mov', 'wmv', 'webm'].includes(ext);

                return (
                  <div key={index} className={styles.fileCard}>
                    <div className={styles.previewContainer}>
                      {isImage ? (
                        <div className={styles.clickableImage} onClick={() => setMagnifiedImage(url)}>
                          <img
                            src={url}
                            alt="Question attachment"
                            className={styles.imagePreview}
                          />
                          <div className={styles.magnifyOverlay}>
                            <span className={styles.magnifyIcon}>🔍</span>
                            <span className={styles.clickHint}>Click to view</span>
                          </div>
                        </div>
                      ) : isAudio ? (
                        <div 
                          className={styles.audioPreview} 
                          onClick={(e) => {
                            const audio = e.currentTarget.querySelector('audio');
                            if (audio && e.target !== audio && !audio.contains(e.target)) {
                              if (audio.paused) {
                                audio.play();
                              } else {
                                audio.pause();
                              }
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={styles.audioIcon}>🎵</div>
                          <audio controls className={styles.audioPlayer}>
                            <source src={url} type={`audio/${ext}`} />
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      ) : isVideo ? (
                        <div 
                          className={styles.clickableImage}
                          onClick={(e) => {
                            const video = e.currentTarget.querySelector('video');
                            if (video && e.target === e.currentTarget) {
                              if (video.paused) {
                                video.play();
                              } else {
                                video.pause();
                              }
                            }
                          }}
                          style={{ cursor: 'pointer', position: 'relative' }}
                        >
                          <video 
                            controls 
                            className={styles.videoPlayer}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <source src={url} type={`video/${ext}`} />
                            Your browser does not support the video element.
                          </video>
                          <div 
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: '48px',
                              cursor: 'pointer',
                              zIndex: 1
                            }}
                            onClick={(e) => {
                              const video = e.currentTarget.parentElement.querySelector('video');
                              if (video) {
                                e.preventDefault();
                                e.stopPropagation();
                                if (video.paused) {
                                  video.play();
                                } else {
                                  video.pause();
                                }
                              }
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Separator between question and answers */}
        <div className={styles.questionAnswersSeparator}></div>

        {/* Answer Options */}
        <div 
          className={styles.answersContainer}
          style={{
            display: currentQuestion.type !== QuestionType.FreeText &&
                     currentQuestion.answers.length > 1 && 
                     currentQuestion.answers.every(ans => ans.staticFileUrls && ans.staticFileUrls.length > 0)
              ? 'grid'
              : 'flex',
            gridTemplateColumns: currentQuestion.type !== QuestionType.FreeText &&
                                 currentQuestion.answers.length > 1 && 
                                 currentQuestion.answers.every(ans => ans.staticFileUrls && ans.staticFileUrls.length > 0)
              ? 'repeat(auto-fill, minmax(220px, max-content))'
              : undefined,
            gap: currentQuestion.type !== QuestionType.FreeText &&
                 currentQuestion.answers.length > 1 && 
                 currentQuestion.answers.every(ans => ans.staticFileUrls && ans.staticFileUrls.length > 0)
              ? 'var(--spacing-sm)'
              : '6px'
          }}
        >
          {currentQuestion.type === QuestionType.FreeText ? (
            <div className={styles.freeTextInputContainer}>
              <textarea
                placeholder="Enter your answer here..."
                className={styles.freeTextInput}
                value={userAnswers[currentQuestion.id]?.[0] || ''}
                onChange={(e) => {
                  const questionId = currentQuestion.id;
                  setUserAnswers(prev => ({
                    ...prev,
                    [questionId]: [e.target.value]
                  }));
                }}
              />
            </div>
          ) : (
            currentQuestion.answers.map((answer, idx) => {
              const isSelected = userAnswers[currentQuestion.id]?.includes(answer.id);
              const allAnswersHaveAttachments = currentQuestion.answers.every(ans => ans.staticFileUrls && ans.staticFileUrls.length > 0);
              
              return (
                <div
                  key={answer.id}
                  className={`${styles.answerOption} ${isSelected ? styles.answerSelected : ''}`}
                  onClick={() => handleAnswerSelect(answer.id)}
                  style={{
                    maxWidth: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? '220px' : undefined,
                    flexDirection: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? 'column' : undefined,
                    alignItems: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? 'flex-start' : undefined,
                    minHeight: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? 'auto' : undefined,
                    height: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? 'auto' : undefined,
                    position: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? 'relative' : undefined
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <input
                      type={currentQuestion.type === QuestionType.MultipleChoice ? "checkbox" : "radio"}
                      id={`answer-${answer.id}`}
                      name={`question-${currentQuestion.id}`}
                      checked={isSelected}
                      onChange={() => {}}
                      className={styles.answerRadio}
                    />
                    <span className={styles.answerLetter}>
                      {String.fromCharCode(65 + idx)}
                    </span>
                  </div>
                  
                  <label
                    htmlFor={`answer-${answer.id}`}
                    className={styles.answerLabel}
                    style={{
                      width: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? '100%' : undefined,
                      flexDirection: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? 'column' : undefined,
                      alignItems: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? 'flex-start' : undefined,
                      paddingLeft: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? '0' : undefined,
                      paddingTop: allAnswersHaveAttachments && currentQuestion.answers.length > 1 && answer.text ? '45px' : undefined
                    }}
                    onClick={(e) => e.preventDefault()}
                  >
                    <div 
                      className={styles.answerContent}
                      style={{
                        width: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? '100%' : undefined,
                        marginLeft: allAnswersHaveAttachments && currentQuestion.answers.length > 1 ? '0' : undefined,
                        marginTop: allAnswersHaveAttachments && currentQuestion.answers.length > 1 && answer.text ? '4px' : '0'
                      }}
                    >
                      {answer.text && <div>{answer.text}</div>}
                      {answer.staticFileUrls && answer.staticFileUrls.length > 0 && (
                      <div 
                        className={styles.answerAttachments}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div 
                          className={styles.filesGrid}
                          style={{
                            gridTemplateColumns: allAnswersHaveAttachments && currentQuestion.answers.length > 1
                              ? '1fr'
                              : answer.staticFileUrls.length > 1 
                                ? 'repeat(auto-fit, minmax(180px, 1fr))' 
                                : '1fr',
                            gap: '4px',
                            paddingTop: '4px'
                          }}
                        >
                          {answer.staticFileUrls.map((url, fileIdx) => {
                            const ext = url.split('.').pop().toLowerCase();
                            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
                            
                            return (
                              <div 
                                key={fileIdx} 
                                className={styles.fileCard}
                                style={{
                                  width: '100%',
                                  marginTop: '0'
                                }}
                              >
                                <div className={styles.previewContainer}>
                                  {isImage ? (
                                    <div className={styles.clickableImage} onClick={() => setMagnifiedImage(url)}>
                                      <img
                                        src={url}
                                        alt={`Answer attachment`}
                                        className={styles.imagePreview}
                                      />
                                      <div className={styles.magnifyOverlay}>
                                        <span className={styles.magnifyIcon}>🔍</span>
                                        <span className={styles.clickHint}>Click to view</span>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    </div>
                  </label>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Navigation Footer */}
      <div className={styles.navigation}>
        <div className={styles.navigationControls}>
          <div className={styles.mainButtons}>
            {currentQuestionIndex > 0 ? (
              <button
                onClick={handlePrevious}
                className={styles.navigationButton}
              >
                ← Previous
              </button>
            ) : (
              <button 
                className={styles.navigationButtonDisabled}
                disabled
              >
                ← Previous
              </button>
            )}
            
            <button
              onClick={() => setShowGoToModal(true)}
              className={styles.goToButton}
              title="Jump to question"
            >
              Go to Q#
            </button>
            
            {currentQuestionIndex < totalQuestions - 1 ? (
              <button
                onClick={handleNext}
                className={styles.navigationButton}
              >
                Next →
              </button>
            ) : (
              <button
                onClick={handleSubmitClick}
                className={styles.submitButton}
              >
                Submit Module
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Go to Q# Modal */}
      {showGoToModal && (
        <div className={styles.modalOverlay} onClick={() => setShowGoToModal(false)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Go to Question</h3>
            </div>
            <div className={styles.modalBody}>
              <div className={`${styles.goToContainer} go-to-container ${showShakeAnimation ? styles.shakeAnimation : ''}`}>
                <label className={styles.goToLabel}>
                  Enter question number (1-{totalQuestions}):
                </label>
                <input
                  type="text"
                  value={goToInput}
                  onChange={handleGoToInputChange}
                  onKeyPress={handleGoToKeyPress}
                  placeholder={`1 - ${totalQuestions}`}
                  className={styles.goToInput}
                  autoFocus
                />
                {showErrorPopup && (
                  <div className={styles.errorMessage} style={{ color: '#d32f2f', marginTop: '8px', fontSize: '14px' }}>
                    ⚠️ Please enter a number between 1 and {totalQuestions}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={() => setShowGoToModal(false)}
                className={`${styles.modalButton} ${styles.modalButtonSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={handleGoToSubmit}
                disabled={!goToInput || isNaN(parseInt(goToInput)) || parseInt(goToInput) < 1 || parseInt(goToInput) > totalQuestions}
                className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
              >
                Go
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitModal && (
        <div className={styles.modal} onClick={() => setShowSubmitModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Submit Assessment?</h3>
            
            {getUnansweredQuestions().length > 0 && (
              <div className={styles.modalWarning}>
                <p>⚠️ You have {getUnansweredQuestions().length} unanswered question(s):</p>
                <div className={styles.unansweredList}>
                  {getUnansweredQuestions().map(({ index }) => (
                    <button
                      key={index}
                      className={styles.unansweredBadge}
                      onClick={() => {
                        setCurrentQuestionIndex(index);
                        setShowSubmitModal(false);
                      }}
                    >
                      Question {index + 1} →
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className={styles.modalText}>
              Are you sure you want to submit? You cannot change your answers after submission.
            </p>

            <div className={styles.modalActions}>
              <button 
                className={styles.button}
                onClick={() => setShowSubmitModal(false)}
              >
                Cancel
              </button>
              <button 
                className={styles.buttonSuccess}
                onClick={handleSubmit}
              >
                Submit Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Magnifier Modal */}
      {magnifiedImage && (
        <div className={styles.imageModal} onClick={() => setMagnifiedImage(null)}>
          <div 
            className={styles.imageModalContent}
            onClick={(e) => {
              if (isDragging) {
                e.stopPropagation();
                return;
              }
              setMagnifiedImage(null);
            }}
          >
            <button
              className={styles.imageCloseButton}
              onClick={() => setMagnifiedImage(null)}
              title="Close"
            >
              ✕
            </button>
            <div 
              ref={imageContainerRef}
              className={styles.imageContainer}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              style={{ 
                cursor: isDragging ? 'grabbing' : 'grab',
                overflow: 'auto'
              }}
            >
              <img
                src={magnifiedImage}
                className={styles.magnifiedImage}
                alt="Magnified preview"
                draggable={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Questions;
